import { useAuth0 } from '@auth0/auth0-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { mintServiceTokens } from '@/lib/api/rome';
import type { ServiceTokens } from '@/lib/types/rome';

// Token refresh threshold (5 minutes before expiry)
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
// Default token TTL (1 hour, matching Rome config)
const DEFAULT_TOKEN_TTL_MS = 60 * 60 * 1000;

export interface ServiceTokensContextValue {
  tokens: ServiceTokens | null;
  isLoading: boolean;
  error: string | null;
  refreshTokens: () => Promise<ServiceTokens | null>;
}

export const ServiceTokensContext = createContext<ServiceTokensContextValue | null>(null);

interface ServiceTokensProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages service-specific JWT tokens.
 * Fetches tokens from Rome once and shares them across all consumers.
 */
export function ServiceTokensProvider({ children }: ServiceTokensProviderProps) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [tokens, setTokens] = useState<ServiceTokens | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);
  const fetchInProgressRef = useRef(false);
  const hasFetchedRef = useRef(false);

  const fetchTokens = useCallback(async (): Promise<ServiceTokens | null> => {
    // Prevent concurrent fetches
    if (fetchInProgressRef.current) {
      return null;
    }

    if (!isAuthenticated) {
      return null;
    }

    fetchInProgressRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await mintServiceTokens(accessToken);

      if (response.status === 200 && response.data) {
        setTokens(response.data);
        hasFetchedRef.current = true;
        return response.data;
      } else {
        setError('Failed to mint service tokens');
        console.error('[ServiceTokensProvider] Failed to mint tokens:', response.status);
        return null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      console.error('[ServiceTokensProvider] Error minting tokens:', err);
      return null;
    } finally {
      setIsLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Schedule refresh before token expiry
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
    }

    const refreshIn = DEFAULT_TOKEN_TTL_MS - TOKEN_REFRESH_THRESHOLD_MS;
    refreshTimeoutRef.current = window.setTimeout(() => {
      fetchTokens();
    }, refreshIn);
  }, [fetchTokens]);

  // Fetch tokens once when authenticated
  useEffect(() => {
    if (isAuthenticated && !hasFetchedRef.current && !fetchInProgressRef.current) {
      fetchTokens();
    }
  }, [isAuthenticated, fetchTokens]);

  // Schedule refresh when tokens are fetched
  useEffect(() => {
    if (tokens) {
      scheduleRefresh();
    }

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [tokens, scheduleRefresh]);

  return (
    <ServiceTokensContext.Provider value={{ tokens, isLoading, error, refreshTokens: fetchTokens }}>
      {children}
    </ServiceTokensContext.Provider>
  );
}

/**
 * Hook to access service tokens from the context.
 * Must be used within a ServiceTokensProvider.
 */
export function useServiceTokens(): ServiceTokensContextValue {
  const context = useContext(ServiceTokensContext);
  if (!context) {
    throw new Error('useServiceTokens must be used within a ServiceTokensProvider');
  }
  return context;
}
